import {
  reportNuveiSdkError,
  reportNuveiSdkSuccess,
} from "@/observability/nuvei";
import type {
  NuveiSafeChargeAddCardUpoRequest,
  NuveiSafeChargeAddCardUpoResult,
  NuveiSafeChargeCreatePaymentOptions,
  NuveiSafeChargeFieldOptions,
  NuveiSafeChargeGetTokenRequest,
  NuveiSafeChargeGetTokenResult,
  NuveiSafeChargePaymentResult,
} from "./safecharge";

// t("global.credit_card_payment_config_error")
const ERROR_KEY = "global.credit_card_payment_config_error";

// Nuvei responds HTTP 200 even on failure; errors are flagged in the body.
// errCode can be 0 or "0" on success, or missing entirely — in which case the
// API-level `status` field ("ERROR") is the only failure signal. `result` is
// the primary discriminator on getToken/addCardUpo ("ADDED" | "ERROR") and
// createPayment ("APPROVED" | "DECLINED" | "ERROR" | "PENDING") responses —
// without checking it, an ERROR result with errCode 0/missing and no `status`
// field was misclassified as success, so a genuine Nuvei-side tokenisation
// failure resolved instead of rejecting, silently dropping the card entirely.
export const isNuveiSuccess = (response: {
  errCode?: number | string;
  status?: string;
  result?: string;
}): boolean =>
  (typeof response.errCode === "undefined" || `${response.errCode}` === "0") &&
  `${response.status ?? ""}`.toUpperCase() !== "ERROR" &&
  `${response.result ?? ""}`.toUpperCase() !== "ERROR";

export type NuveiSafeChargeCreatePaymentRequest = {
  userTokenId: number;
  sessionToken: string;
  merchantId: string;
  merchantSiteId: string;
  env: string;
  cardHolderName: string;
  savedPaymentAccountPspToken?: string;
  userPaymentOptionId?: string;
  clientUniqueId?: string;
  //Maybe its not important here open order already set this details
  billingAddress?: {
    email: string;
    country: string;
    [key: string]: string;
  };
};
export type NuveiSafeChargeCreatePaymentPaymentOption = {
  userPaymentOptionId: string;
  card?: NuveiSafeChargeFieldOptions;
};

export const createCreditCardPaymentAsync = (
  request: NuveiSafeChargeCreatePaymentRequest
) => {
  const paymentRequest: NuveiSafeChargeCreatePaymentOptions = {
    merchantId: request.merchantId,
    merchantSiteId: request.merchantSiteId,
    env: request.env,
    sessionToken: request.sessionToken,
    userTokenId: request.userTokenId,
    clientUniqueId: request.clientUniqueId,
    cardHolderName: request.cardHolderName,
    billingAddress: request.billingAddress,
    // IMPORTANT: to able to confirm SCA BE initiated order we need to send userPaymentOptionId (every time),
    // to SCA able to get back card data from Nuvei callback we need to set Nuvei Fields scard object to this card property
    paymentOption: request.savedPaymentAccountPspToken
      ? {
          userPaymentOptionId: request.savedPaymentAccountPspToken,
        }
      : {
          userPaymentOptionId: request.userPaymentOptionId,
          card: window.scard,
        },
  };

  return new Promise<NuveiSafeChargePaymentResult>((resolve, reject) => {
    if (!window.safeChargeSdkInstance) {
      reject({ message: ERROR_KEY });
      return;
    }

    window.safeChargeSdkInstance.createPayment(paymentRequest, (response) => {
      if (isNuveiSuccess(response)) {
        // TEMP: verify Datadog RUM/Logs instrumentation in real payment flow.
        reportNuveiSdkSuccess("createPayment", response);
        resolve(response);
      } else {
        reportNuveiSdkError("createPayment", response);
        reject({ message: response.errorDescription, response });
      }
    });
  });
};

export const addCardUpoAsync = (
  req: Omit<NuveiSafeChargeAddCardUpoRequest, "useFields">
) => {
  const request: NuveiSafeChargeAddCardUpoRequest = {
    useFields: true,
    ...req,
  };

  return new Promise<NuveiSafeChargeAddCardUpoResult>((resolve, reject) => {
    if (!window.safeChargeSdkInstance) {
      reject({ message: ERROR_KEY });
      return;
    }

    window.safeChargeSdkInstance.addCardUpo(request, (response) => {
      if (isNuveiSuccess(response)) {
        // TEMP: verify Datadog RUM instrumentation — remove once confirmed.
        reportNuveiSdkSuccess("addCardUpo", response);
        resolve(response);
      } else {
        reportNuveiSdkError("addCardUpo", response);
        reject({ message: response.errorDescription, response });
      }
    });
  });
};

export const getTokenResultAsync = (
  scard: any,
  request: NuveiSafeChargeGetTokenRequest
) => {
  return new Promise<NuveiSafeChargeGetTokenResult>((resolve, reject) => {
    if (!window.safeChargeSdkInstance) {
      reject({ message: ERROR_KEY });
      return;
    }

    // The SDK's getToken() returns a minimal thenable, not a spec-compliant
    // Promise — its `.then()` does not return a chainable promise. Chaining
    // `.catch()` after `.then()` throws "Cannot read properties of undefined
    // (reading 'catch')" synchronously, rejecting immediately before the
    // underlying request even completes. Pass both handlers into the single
    // `.then(onFulfilled, onRejected)` call instead, which works on any
    // thenable regardless of chaining support.
    window.safeChargeSdkInstance.getToken(scard, request).then(
      (response: any) => {
        if (isNuveiSuccess(response)) {
          // TEMP: verify Datadog RUM/Logs instrumentation in real token flow.
          reportNuveiSdkSuccess("getToken", response);
          resolve(response);
        } else {
          reportNuveiSdkError("getToken", response);
          // message must be a stable string key — downstream error handling
          // treats it as an i18n key (e.g. i18n.exists(error)). The raw
          // response object is kept in `response` for diagnostics only.
          reject({ message: ERROR_KEY, response });
        }
      },
      (error: unknown) => {
        reportNuveiSdkError("getToken", error);
        reject({ message: ERROR_KEY, response: error });
      }
    );
  });
};
